export const PAYMENT_COMPLETE_MESSAGE = "PAYU_PAYMENT_COMPLETE";
export const FORWARD_TO_IFRAME_MESSAGE = "FORWARD_TO_IFRAME";
export const PAYMENT_RESULT_STORAGE_KEY = "bankco_payu_payment_result";
export const PAYMENT_RESULT_CHANNEL = "bankco_payu_payment_channel";
// window.name is one of the few properties a cross-origin window reference
// can still read (alongside .closed, .location, .postMessage...) — it
// survives even when window.opener has been severed by PayU's redirect
// AND is completely unaffected by same-origin storage partitioning (it's
// not a storage API at all). The host page holds a direct reference to
// the popup it opened, so it can read this on every poll tick regardless
// of either failure mode.
export const PAYMENT_NAME_SIGNAL_PREFIX = "bankco_payu_result:";

// Shared by every SDK-context order-status poll (gift cards, brand bundles,
// ...). PayU's own hosted-checkout session stays valid for ~15 minutes, so
// a poll that gives up sooner can end up timing out while the user is still
// legitimately mid-payment on PayU's page.
export const ORDER_STATUS_POLL_INTERVAL_MS = 5000;
export const ORDER_STATUS_POLL_TIMEOUT_MS = 15 * 60 * 1000;
export const ORDER_STATUS_POLL_MAX_ATTEMPTS = Math.floor(
  ORDER_STATUS_POLL_TIMEOUT_MS / ORDER_STATUS_POLL_INTERVAL_MS
);

// Same debug flag/key the integrator's host page (productMarketplace)
// uses, so turning it on once (?debugPaymentBridge=1 or
// localStorage.debug_payment_bridge = "1") surfaces logs from every hop —
// popup, iframe, and host — in one console.
const PAYMENT_DEBUG_STORAGE_KEY = "debug_payment_bridge";
const PAYMENT_DEBUG_QUERY_PARAM = "debugPaymentBridge";

export const isPaymentBridgeDebugEnabled = () => {
  if (typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get(PAYMENT_DEBUG_QUERY_PARAM) === "1") {
      // Write-through to localStorage: a client-side route change (e.g.
      // landing on "/" with the flag, then navigating into the reward
      // detail page) drops the query string, but this keeps the flag on
      // for the rest of this same-origin session without needing it in
      // the URL. Off by default for every real user — this only ever
      // turns on if someone explicitly puts it in the URL or storage.
      try { window.localStorage.setItem(PAYMENT_DEBUG_STORAGE_KEY, "1"); } catch { /* ignore */ }
      return true;
    }
    return window.localStorage.getItem(PAYMENT_DEBUG_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

export const debugPaymentBridge = (label, payload) => {
  if (!isPaymentBridgeDebugEnabled()) return;
  const context = window.self !== window.top ? "iframe" : "tab";
  // eslint-disable-next-line no-console
  console.info(`[payment-bridge] [${context}] ${label}`, payload);
};

export const isPaymentPopupWindow = () => {
  if (typeof window === "undefined") return false;
  return Boolean(window.opener) || window.name?.startsWith("payu_payment") || document.referrer.includes("payu.in");
};

export const closePaymentPopup = (precomputedIsPopup, delayMs = 0) => {
  // Accept a precomputed flag because notifyPaymentCompleteAndClose
  // overwrites window.name with the result payload (see below) before
  // calling this — checking isPaymentPopupWindow() fresh at that point
  // would see the new name instead of the original "payu_payment_..."
  // one and wrongly conclude this isn't a popup.
  const isPopup = typeof precomputedIsPopup === "boolean" ? precomputedIsPopup : isPaymentPopupWindow();
  debugPaymentBridge("closePaymentPopup called", {
    isPopup,
    windowOpener: Boolean(window.opener),
    windowName: window.name,
    referrer: document.referrer,
    delayMs,
  });
  if (!isPopup) return;

  // Give the host page's popup-close poll (which reads window.name — see
  // the WINDOW.NAME SIGNAL above) a couple of ticks to actually read it
  // before this window disappears out from under it.
  window.setTimeout(() => {
    let attempts = 0;
    const closeTimer = window.setInterval(() => {
      attempts += 1;
      try {
        window.close();
      } catch {
        // ignore blocked close attempts
      }

      try {
        window.open("", "_self")?.close();
      } catch {
        // ignore blocked close attempts
      }

      if (attempts >= 5 || window.closed) {
        window.clearInterval(closeTimer);
        debugPaymentBridge("closePaymentPopup finished", { attempts, closed: window.closed });
      }
    }, 300);
  }, delayMs);
};

export const buildCleanLocationPath = () => {
  if (typeof window === "undefined") return "";

  const nextUrl = new URL(window.location.href);
  nextUrl.searchParams.delete("first_time");

  return `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
};

export const notifyPaymentCompleteAndClose = ({
  orderId = "",
  orderPath = "",
  orderUrl = "",
  status = "success",
  source = "bankco",
} = {}) => {
  if (typeof window === "undefined") return;

  // Capture popup-ness BEFORE we overwrite window.name below — otherwise
  // closePaymentPopup()'s own isPaymentPopupWindow() check would look at
  // the already-mutated name and think this was never a popup.
  const wasPopupWindow = isPaymentPopupWindow();

  const cleanCurrentPath = buildCleanLocationPath();
  const resolvedOrderPath = String(orderPath || cleanCurrentPath || "").trim();
  const resolvedOrderUrl = String(orderUrl || "").trim() || (
    resolvedOrderPath
      ? `${window.location.origin}${resolvedOrderPath.startsWith("/") ? resolvedOrderPath : `/${resolvedOrderPath}`}`
      : window.location.href
  );

  const message = {
    type: PAYMENT_COMPLETE_MESSAGE,
    orderId: String(orderId || ""),
    status,
    orderPath: resolvedOrderPath,
    orderUrl: resolvedOrderUrl,
    source,
    // ✅ unique stamp so the parent can detect a NEW result even if an old
    // one is still sitting in localStorage from a previous attempt
    completedAt: Date.now(),
  };

  debugPaymentBridge("notifyPaymentCompleteAndClose", message);

  // ✅ WINDOW.NAME SIGNAL — the one channel that survives BOTH a severed
  // window.opener (PayU's cross-origin redirect) AND storage partitioning
  // (it isn't a storage API). The host page reads payuPopupRef.current.name
  // on its existing popup-close poll — a cross-origin read of .name is
  // always allowed, unlike .location or most other properties.
  try {
    window.name = `${PAYMENT_NAME_SIGNAL_PREFIX}${JSON.stringify(message)}`;
    debugPaymentBridge("window.name signal set", { name: window.name });
  } catch (e) {
    debugPaymentBridge("window.name signal failed", String(e));
  }

  // ✅ PRIMARY SIGNAL — works regardless of window.opener, popup vs tab,
  // or any browser quirk. The parent window polls/listens for this key.
  try {
    window.localStorage.setItem(PAYMENT_RESULT_STORAGE_KEY, JSON.stringify(message));
    // storage event only fires in OTHER tabs/windows, which is exactly
    // what we want here (the parent tab, not this one)
  } catch {
    // localStorage unavailable (rare, e.g. private mode edge cases) —
    // fall back silently to the postMessage-only path below
  }

  // ✅ BROADCASTCHANNEL SIGNAL — same-origin cross-tab relay to any other
  // bankco.com browsing context (namely the SDK iframe). Unlike the
  // storage event, it doesn't require a write to actually change value
  // between attempts and isn't throttled/deferred in the same way, so we
  // prefer it where supported (all evergreen browsers except old Safari).
  // NOTE: this can ONLY reach other bankco.com contexts (the iframe) — it
  // can never reach the integrator's own top-level page directly, since
  // BroadcastChannel/localStorage are same-origin only.
  try {
    const channel = new BroadcastChannel(PAYMENT_RESULT_CHANNEL);
    channel.postMessage(message);
    channel.close();
    debugPaymentBridge("BroadcastChannel posted", { channel: PAYMENT_RESULT_CHANNEL });
  } catch (e) {
    debugPaymentBridge("BroadcastChannel unsupported/failed", String(e));
  }

  // ✅ SECONDARY SIGNAL — fires immediately if window.opener happens to be intact.
  // This is the ONLY channel that can reach the integrator's top-level page
  // directly (bypassing the iframe entirely), IF the host page opened this
  // popup itself. It commonly breaks after a cross-origin redirect through
  // PayU (COOP / browser opener-severing) — don't assume it fired.
  debugPaymentBridge("opener check", { hasOpener: Boolean(window.opener), openerClosed: window.opener?.closed });
  if (window.opener && !window.opener.closed) {
    window.opener.postMessage(message, "*");
    window.opener.postMessage({ type: FORWARD_TO_IFRAME_MESSAGE, payload: message }, "*");
    debugPaymentBridge("posted to window.opener", message);
  }

  // ✅ EMBEDDED SIGNAL — notifies the host page when Bankco is running in an iframe.
  if (window.parent && window.parent !== window) {
    window.parent.postMessage(message, "*");
    window.parent.postMessage({ type: FORWARD_TO_IFRAME_MESSAGE, payload: message }, "*");
    debugPaymentBridge("posted to window.parent", message);
  }

  // Only delay closing when window.opener is already gone — that's the
  // one case where nothing faster (the opener postMessage above) could
  // have already reached whoever needs this result, so the window.name
  // poll fallback is the only thing that still can. When opener IS
  // intact (the common case for a normal, non-SDK visit — and often for
  // SDK flows too), that signal already landed synchronously above, so
  // there's no reason to delay closing at all; this keeps normal-flow
  // popup-closing exactly as fast as it was before this fallback existed.
  const hasIntactOpener = Boolean(window.opener) && !window.opener.closed;
  const closeDelayMs = hasIntactOpener ? 0 : 1500; // ≈ 2-3 host poll ticks (600ms each)
  closePaymentPopup(wasPopupWindow, closeDelayMs);
};
